/**
 * /tryon-studio — standalone, full-bleed virtual try-on studio.
 *
 * Same engine as /tryon, presented as a dedicated studio: the site chrome is
 * suppressed (see App.tsx), the stage fills the viewport, and LiveTryonStage
 * runs in `studio` mode — softer auto-hiding alignment guide, toggleable
 * refine-fit, fullscreen + frame carousel, no purchase CTA row.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Product } from '../types';
import { useOrg, useTryonStatus } from '../context/OrgContext';
import { siteTitle } from '../utils/config';
import { loadTryonCatalogue } from '../lib/tryon/catalogue';
import LiveTryonStage from '../components/tryon/LiveTryonStage';
import TryonFeatures from '../components/tryon/TryonFeatures';

const TryonStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedId = searchParams.get('productId');
  const org = useOrg();
  const tryonStatus = useTryonStatus();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Re-applied whenever the org resolves: OrgContext sets the site title when its
  // fetch lands, which happens AFTER this page mounts and would otherwise clobber
  // the try-on title. Restores the site title on unmount so navigating away
  // doesn't leave "Virtual Try-On" in the tab.
  useEffect(() => {
    document.title = `Virtual Try-On - ${siteTitle(org?.org_name)}`;
    return () => { document.title = siteTitle(org?.org_name); };
  }, [org]);

  useEffect(() => {
    if (tryonStatus !== 'enabled') return;
    let cancelled = false;
    loadTryonCatalogue().then((frames) => {
      if (cancelled) return;
      setProducts(frames);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tryonStatus]);

  const target = requestedId
    ? products.find((p) => String(p.id) === requestedId) ?? null
    : null;

  if (tryonStatus === 'loading') {
    return (
      <div className="tryon-studio-boot">
        <div className="tryon-studio-boot__text">Loading Engine…</div>
      </div>
    );
  }

  if (tryonStatus === 'disabled') {
    return (
      <div className="empty-state" style={{ minHeight: '60vh' }}>
        <h2>Virtual Try-On is not available</h2>
        <p>This feature is currently disabled for this store.</p>
        <button className="btn btn--primary" onClick={() => navigate('/products')}>Browse Products</button>
      </div>
    );
  }

  return (
    <div className="tryon-page tryon-page--studio">
      <div className="tryon-page__header">
        <div>
          <h1 className="tryon-page__title">OPTIKORV TRYON</h1>
        </div>
      </div>
      <div className="tryon-page__stage">
        <LiveTryonStage
          products={products}
          loadingProducts={loading}
          targetProduct={target}
          onViewDetails={(p) => navigate(`/product/${p.id}`)}
          studio
        />
      </div>

      <TryonFeatures />
    </div>
  );
};

export default TryonStudioPage;
